<template>
  <NavigationBarTop />
  <div class="gallery-page-container">
    <h2 class="page-title"> Gallery: Our Craftsmanship </h2>
    
    <form @submit.prevent class="filter-form">
        <select v-model="selectedCategory" class="category-select">
            <option value="">All Arts & Styles</option>
            <option value="hair">Hair</option>
            <option value="nails">Nails</option>
            <option value="skin">Skin</option>
            <option value="misc">Miscellaneous</option> 
        </select>
      </form>
      
      <div class="gallery_list">
        <div class="g_images" v-for="img in filteredGallery" :key="img.id">
          <img 
            :src="getImageUrl(img.image_path)" 
            :alt="img.caption" 
            class="gallery-image"
          >
          <div class="caption-overlay">
            <p class="image-caption">{{ img.caption }}</p>
          </div>
        </div>
        
        <p v-if="filteredGallery.length === 0" class="no-images-msg">
            No masterpieces found in this category. Expand your search.
        </p>
      </div>
  </div>
   
</template>

<script>
import NavigationBarTop from '../components/NavigationBarTop.vue'
const GALLERY_STORAGE_KEY = 'salonGallery'; 

export default {
    name: 'Gallery',
    components: {
        NavigationBarTop
    },
    data() {
        return {
            gallery: [], // Will be populated from localStorage
            selectedCategory: '' // State for filtering
        }
    },
    computed: {
        // Filters the gallery items based on the selected category
        filteredGallery() {
            if (!this.selectedCategory) {
                return this.gallery;
            }
            return this.gallery.filter(img => img.category === this.selectedCategory);
        }
    },
    created() {
        this.loadGallery();
    },
    methods: {
        loadGallery() {
            const storedGallery = localStorage.getItem(GALLERY_STORAGE_KEY);
            if (storedGallery) {
                this.gallery = JSON.parse(storedGallery);
            } else {
                console.warn("Gallery data not found in local storage. Please check the admin panel.");
                this.gallery = [];
            }
        },
        getImageUrl(imagePath) {
            const filename = imagePath.split('/').pop();
            
            try {
                 // Assumes image assets are in src/assets/gallery/images/
                 return require(`@/assets/gallery/images/${filename}`);
            } catch (e) {
                 // Fallback if the image file doesn't exist in the project
                 console.error(`Image file not found: ${filename}`);
                 return ''; 
            }
        }
    }
}
</script>

<style scoped>

.gallery-page-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* --- Title --- */
.page-title {
    font-family: var(--font-heading);
    font-size: 2.5rem;
    font-weight: 700;
    color: #795548; 
    text-align: center;
    margin-top: 2rem;
    margin-bottom: 2.5rem; 
    text-transform: uppercase;
    position: relative;
}

.page-title::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: #FFC107;
    margin: 10px auto 0;
    border-radius: 2px;
}

/* --- Filtering Form --- */
.filter-form {
    display: flex;
    justify-content: center;
    padding: 0 1rem;
    margin-bottom: 3rem; /* More space below filter */
}

.category-select {
    font-family: var(--font-body);
    padding: 12px 20px;
    box-sizing: border-box;
    border: 2px solid #795548;
    background-color: #FFF8E1; /* Light cream background */
    color: #333333;
    border-radius: 25px; /* Pill-shaped dropdown */
    width: 100%; 
    max-width: 350px; 
    cursor: pointer;
    font-size: 1.05rem;
    font-weight: 600;
    appearance: none; /* Remove default arrow */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23795548'%3E%3Cpath d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 15px center;
    background-size: 1em;
    transition: all 0.3s;
}

.category-select:hover {
    border-color: #FFC107;
    box-shadow: 0 0 5px rgba(255, 193, 7, 0.5);
}


/* --- Gallery Grid --- */
.gallery_list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    justify-content: center;
    padding: 20px;
    gap: 30px; /* Increased gap */
}

.g_images {
    width: 100%; /* Use grid column width */
    height: auto;
    background: white;
    border-radius: 12px; /* Softer rounding */
    overflow: hidden;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15); /* Richer shadow */
    transition: transform 0.3s, box-shadow 0.3s;
    cursor: pointer;
    border: 2px solid #e0e0e0;
    position: relative;
}

.g_images:hover {
    transform: translateY(-8px); /* Deeper lift on hover */
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
}

.gallery-image {
    width: 100%;
    height: 300px; /* Increased height for impact */
    object-fit: cover; 
    display: block;
}

.caption-overlay {
    background-color: #D32F2F; /* Rich red background */
    color: #fdfdfd;
    padding: 10px;
    text-align: center;
    transition: background-color 0.3s;
}

.g_images:hover .caption-overlay {
    background-color: #795548; /* Change color on hover for elegance */
}

.image-caption {
    padding: 5px 0;
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    font-family: var(--font-heading);
}

.no-images-msg {
    grid-column: 1 / -1;
    color: #795548;
    text-align: center;
    padding: 50px;
    font-size: 1.2rem;
    font-style: italic;
    border: 1px dashed #e0e0e0;
    border-radius: 10px;
    margin: 30px;
}

/* --- Responsiveness --- */
@media (max-width: 600px) {
    .gallery-page-container {
        padding: 0 10px;
    }
    
    .page-title {
        font-size: 2rem;
        margin-bottom: 2rem;
    }

    .gallery_list {
        padding: 10px;
        gap: 20px;
        grid-template-columns: 1fr; /* Single column on small screens */
    }
    
    .gallery-image {
        height: 280px;
    }
}
</style>